<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref, watch } from "vue"
import { storeToRefs } from "pinia"
import { useI18n } from "vue-i18n"
import { useBuildingStore } from "@core/stores/building-v2.store"
import type { PvSegmentParam } from "@modules/pv/types/dimensionnement-pv.types"
import {
    getBuildingCadastreLocationRaw,
    toCadastreParcelFeature,
} from "@modules/pv/utils/buildingCadastreMapGeojson"

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        /** Libellé du scénario (nom d’analyse). */
        scenarioName: string
        /** Pans / segments issus de `inputs.list_params_pv`. */
        segments: PvSegmentParam[] | null | undefined
        /** Centre carte `[lat, lng]` (bâtiment ou premier segment géolocalisé). */
        addressLocation?: [number, number] | null
        /** Désactive le bouton de relance (ex. soumission en cours). */
        disabled?: boolean
    }>(),
    {
        segments: () => [],
        addressLocation: null,
        disabled: false,
    }
)

const emit = defineEmits<{
    (event: "click:relaunch", segments: PvSegmentParam[]): void
}>()

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            /** Aligné sur `lang/pv/components.lang.js` → `pv_segment_parameters_card`. */
            title: "Paramètres",
            /** Aligné sur `pv_segment_selector` (legacy). */
            googleSegments: "Pans Google Solar",
            manualSegments: "Pans ajoutés",
            roofSegment: "Pan de toit",
            roofProperties: "Propriétés du toit",
            segmentParameters: "Paramètres de la sous-installation",
            area: "Aire",
            angle: "Angle d'inclinaison",
            aspect: "Orientation",
            peakpower: "Puissance installée",
            pvtechchoice: "Technologie PV",
            loss: "Pertes système",
            optimal: "Optimisé",
            relaunch: "Relancer la simulation",
            emptySelection:
                "Veuillez valider au moins un pan de toit pour pouvoir relancer la simulation",
            cadastreParcel: "Parcelle cadastrale",
        },
    },
})

const buildingStore = useBuildingStore()
const { building } = storeToRefs(buildingStore)

// ================================
// ÉTAT LOCAL
// ================================
/** Identifiants des segments inclus dans la prochaine relance (par défaut : tous). */
const selectedIds = ref<Set<string>>(new Set())
/** Pan affiché (détail à droite), aligné sur le sélecteur legacy. */
const displayedSegmentId = ref<string | null>(null)

// ================================
// DONNÉES CALCULÉES
// ================================
const getSegmentsList = computed(() => props.segments ?? [])

/**
 * Indique un segment issu de la géolocalisation Google (point GPS présent), comme en legacy.
 */
function isGoogleSegment(segment: PvSegmentParam): boolean {
    return Array.isArray(segment.location) && segment.location.length >= 2
}

const getGoogleSegments = computed(() =>
    getSegmentsList.value.filter((s) => isGoogleSegment(s))
)

const getManualSegments = computed(() =>
    getSegmentsList.value.filter((s) => !isGoogleSegment(s))
)

const getDisplayedSegment = computed((): PvSegmentParam | null => {
    const id = displayedSegmentId.value
    if (!id) return null
    return getSegmentsList.value.find((s) => s.id === id) ?? null
})

/** Segments carte (pastilles numérotées), alignés sur `PVRoofMap` / formulaire toiture. */
const getRoofMapSegments = computed(() => {
    return getGoogleSegments.value.map((segment, index) => {
        const loc = segment.location as [number, number]
        return {
            title: getSegmentListLabel(segment),
            content: [
                {
                    label: t("area"),
                    value: segment.area != null ? `${segment.area} m²` : "—",
                },
                {
                    label: t("aspect"),
                    value:
                        segment.aspect != null && !Number.isNaN(segment.aspect)
                            ? `${segment.aspect}°`
                            : "—",
                },
                {
                    label: t("angle"),
                    value:
                        segment.angle != null && !Number.isNaN(segment.angle)
                            ? `${segment.angle}°`
                            : "—",
                },
            ],
            index: index + 1,
            coordinates: loc,
            isActive: isSegmentSelected(segment.id),
        }
    })
})

const getCadastreParcelMapFeature = computed(() =>
    toCadastreParcelFeature(
        getBuildingCadastreLocationRaw(building.value),
        t("cadastreParcel")
    )
)

const getMapCenter = computed<[number, number] | null>(() => {
    if (props.addressLocation) return props.addressLocation
    const withLoc = getGoogleSegments.value[0]
    if (withLoc?.location && Array.isArray(withLoc.location)) {
        return withLoc.location as [number, number]
    }
    return null
})

const getHasSelection = computed(() => selectedIds.value.size > 0)

/** Affichage brut de la technologie, comme le legacy (pas de libellé étendu). */
const getDisplayedPvtechValue = computed((): string => {
    const c = getDisplayedSegment.value?.pvtechchoice
    if (c == null || c === "") return "—"
    return String(c)
})

const getPanelHeader = computed(() => `${props.scenarioName} • ${t("title")}`)

/**
 * Libellé « Pan de toit n » (indices distincts listes Google / manuel, comme le legacy).
 */
function getSegmentListLabel(segment: PvSegmentParam): string {
    if (isGoogleSegment(segment)) {
        const i = getGoogleSegments.value.findIndex((s) => s.id === segment.id)
        return `${t("roofSegment")} ${i >= 0 ? i + 1 : segment.index + 1}`
    }
    const i = getManualSegments.value.findIndex((s) => s.id === segment.id)
    const n =
        i >= 0 ? getGoogleSegments.value.length + i + 1 : segment.index + 1
    return `${t("roofSegment")} ${n}`
}

function getIsOptimalAngles(segment: PvSegmentParam | null): boolean {
    if (!segment) return false
    return Number(segment.optimalangles) === 1
}

// ================================
// SYNCHRONISATION
// ================================
watch(
    () => props.segments,
    (list) => {
        const next = new Set<string>()
        for (const s of list ?? []) {
            if (typeof s.id === "string" && s.id.length > 0) {
                next.add(s.id)
            }
        }
        selectedIds.value = next.size > 0 ? next : new Set()

        const first = list?.[0]
        const firstId = first && typeof first.id === "string" ? first.id : null
        if (
            !displayedSegmentId.value ||
            !list?.some((s) => s.id === displayedSegmentId.value)
        ) {
            displayedSegmentId.value = firstId
        }
    },
    { immediate: true, deep: true }
)

// ================================
// INTERACTIONS
// ================================
function isSegmentSelected(id: string): boolean {
    return selectedIds.value.has(id)
}

function onToggleSegment(id: string, checked: boolean): void {
    const next = new Set(selectedIds.value)
    if (checked) next.add(id)
    else next.delete(id)
    selectedIds.value = next
}

function onSelectDisplayedSegment(id: string): void {
    displayedSegmentId.value = id
}

function onClickRelaunch(): void {
    if (!getHasSelection.value) return
    const chosen = getSegmentsList.value.filter((s) =>
        selectedIds.value.has(s.id)
    )
    emit("click:relaunch", chosen)
}
</script>

<template>
    <BaseTitledCard
        class="w-full overflow-hidden border border-surface-200 shadow-sm"
        content-class="!p-0"
        header-class="!text-left"
    >
        <template #header>
            {{ getPanelHeader }}
        </template>
        <template #content>
            <div class="flex flex-col items-center gap-8">
                <div
                    class="flex w-full min-w-0 flex-col gap-4 md:flex-row md:items-stretch md:gap-4"
                >
                    <div
                        class="flex min-w-0 w-full flex-1 flex-col gap-2 min-[500px]:min-h-0 min-[500px]:flex-row min-[500px]:items-stretch min-[500px]:gap-2 md:w-1/2"
                    >
                        <div
                            class="flex w-full min-w-0 flex-col gap-1 min-[500px]:w-auto min-[500px]:shrink-0"
                        >
                            <p
                                v-if="getGoogleSegments.length > 0"
                                class="m-0 text-sm font-medium text-surface-800"
                            >
                                {{ t("googleSegments") }}
                            </p>
                            <div
                                v-for="segment in getGoogleSegments"
                                :key="`g-${segment.id}`"
                                class="flex items-center gap-0 rounded border border-transparent px-1 py-0.5"
                                :class="{
                                    'border-primary-300 bg-primary-50/60':
                                        displayedSegmentId === segment.id,
                                }"
                            >
                                <BaseCheckbox
                                    :input-id="`pv-dim-seg-${segment.id}`"
                                    :binary="true"
                                    :model-value="isSegmentSelected(segment.id)"
                                    :disabled="disabled"
                                    @update:model-value="
                                        (v: boolean) =>
                                            onToggleSegment(
                                                segment.id,
                                                Boolean(v)
                                            )
                                    "
                                />
                                <div class="min-w-0 flex-1">
                                    <BaseButton
                                        type="button"
                                        variant="link"
                                        class="h-auto min-h-0 w-full p-2! text-left"
                                        :label="getSegmentListLabel(segment)"
                                        :pt="{
                                            label: 'whitespace-normal break-words',
                                        }"
                                        @click="
                                            onSelectDisplayedSegment(segment.id)
                                        "
                                    />
                                </div>
                            </div>

                            <BaseDivider
                                v-if="
                                    getGoogleSegments.length > 0 &&
                                    getManualSegments.length > 0
                                "
                            />

                            <p
                                v-if="getManualSegments.length > 0"
                                class="m-0 text-sm font-medium text-surface-800"
                            >
                                {{ t("manualSegments") }}
                            </p>
                            <div
                                v-for="segment in getManualSegments"
                                :key="`m-${segment.id}`"
                                class="flex items-center gap-0 rounded border border-transparent px-1 py-0.5"
                                :class="{
                                    'border-primary-300 bg-primary-50/60':
                                        displayedSegmentId === segment.id,
                                }"
                            >
                                <BaseCheckbox
                                    :input-id="`pv-dim-seg-${segment.id}`"
                                    :binary="true"
                                    :model-value="isSegmentSelected(segment.id)"
                                    :disabled="disabled"
                                    @update:model-value="
                                        (v: boolean) =>
                                            onToggleSegment(
                                                segment.id,
                                                Boolean(v)
                                            )
                                    "
                                />
                                <div class="min-w-0 flex-1">
                                    <BaseButton
                                        type="button"
                                        variant="link"
                                        class="h-auto min-h-0 w-full p-2! text-left"
                                        :label="getSegmentListLabel(segment)"
                                        :pt="{
                                            label: 'whitespace-normal break-words',
                                        }"
                                        @click="
                                            onSelectDisplayedSegment(segment.id)
                                        "
                                    />
                                </div>
                            </div>
                        </div>

                        <div
                            class="min-w-0 flex-1 rounded border border-surface-200 bg-white px-4 py-0"
                        >
                            <div class="flex flex-col gap-4">
                                <div class="min-w-0">
                                    <p
                                        class="m-0 py-2.5 text-sm font-medium text-primary-800"
                                    >
                                        {{ t("roofProperties") }}
                                    </p>
                                    <div
                                        class="flex flex-col border-t border-surface-200"
                                    >
                                        <div
                                            class="flex items-center justify-between gap-2 py-2.5 text-sm"
                                        >
                                            <span class="text-surface-600">{{
                                                t("area")
                                            }}</span>
                                            <span
                                                class="shrink-0 text-right text-surface-900"
                                            >
                                                {{
                                                    getDisplayedSegment?.area !=
                                                    null
                                                        ? `${getDisplayedSegment.area}m2`
                                                        : "—"
                                                }}
                                            </span>
                                        </div>
                                        <div
                                            class="flex items-center flex-wrap justify-between gap-2 border-t border-surface-200 py-2.5 text-sm"
                                        >
                                            <span class="text-surface-600">{{
                                                t("angle")
                                            }}</span>
                                            <span
                                                class="shrink-0 text-right text-surface-900"
                                            >
                                                <template
                                                    v-if="
                                                        getIsOptimalAngles(
                                                            getDisplayedSegment
                                                        )
                                                    "
                                                    >{{
                                                        t("optimal")
                                                    }}&nbsp;</template
                                                >
                                                {{
                                                    getDisplayedSegment?.angle ??
                                                    "—"
                                                }}°
                                            </span>
                                        </div>
                                        <div
                                            class="flex items-center flex-wrap justify-between gap-2 border-t border-surface-200 py-2.5 text-sm"
                                        >
                                            <span class="text-surface-600">{{
                                                t("aspect")
                                            }}</span>
                                            <span
                                                class="shrink-0 text-right text-surface-900"
                                            >
                                                <template
                                                    v-if="
                                                        getIsOptimalAngles(
                                                            getDisplayedSegment
                                                        )
                                                    "
                                                    >{{
                                                        t("optimal")
                                                    }}&nbsp;</template
                                                >
                                                {{
                                                    getDisplayedSegment?.aspect ??
                                                    "—"
                                                }}°
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div class="min-w-0">
                                    <p
                                        class="m-0 py-2.5 text-sm font-medium text-primary-800"
                                    >
                                        {{ t("segmentParameters") }}
                                    </p>
                                    <div
                                        class="flex flex-col border-t border-surface-200"
                                    >
                                        <div
                                            class="flex items-center justify-between gap-2 py-2.5 text-sm"
                                        >
                                            <span class="text-surface-600">{{
                                                t("peakpower")
                                            }}</span>
                                            <span
                                                class="shrink-0 text-right text-surface-900"
                                            >
                                                {{
                                                    getDisplayedSegment?.peakpower !=
                                                    null
                                                        ? `${getDisplayedSegment.peakpower} kWc`
                                                        : "—"
                                                }}
                                            </span>
                                        </div>
                                        <div
                                            class="flex items-center flex-wrap justify-between gap-2 border-t border-surface-200 py-2.5 text-sm"
                                        >
                                            <span class="text-surface-600">{{
                                                t("pvtechchoice")
                                            }}</span>
                                            <span
                                                class="shrink-0 text-right text-surface-900"
                                            >
                                                {{ getDisplayedPvtechValue }}
                                            </span>
                                        </div>
                                        <div
                                            class="flex items-center flex-wrap justify-between gap-2 border-t border-surface-200 py-2.5 text-sm"
                                        >
                                            <span class="text-surface-600">{{
                                                t("loss")
                                            }}</span>
                                            <span
                                                class="shrink-0 text-right text-surface-900"
                                            >
                                                {{
                                                    getDisplayedSegment?.loss !=
                                                    null
                                                        ? `${getDisplayedSegment.loss}%`
                                                        : "—"
                                                }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        class="flex min-h-[23rem] w-full min-w-0 flex-col gap-2 md:w-1/2"
                    >
                        <PVRoofMap
                            v-if="getMapCenter"
                            :address-location="getMapCenter"
                            :segments="getRoofMapSegments"
                            :cadastre-feature="getCadastreParcelMapFeature"
                            map-height="23rem"
                            map-container-class="w-full flex-1 overflow-hidden rounded-lg border border-surface-200 shadow-sm"
                            default-tile-layer-type="satellite"
                        />
                    </div>
                </div>

                <BaseMessage
                    v-if="!getHasSelection"
                    severity="error"
                    :closable="false"
                >
                    {{ t("emptySelection") }}
                </BaseMessage>

                <div class="flex justify-center">
                    <BaseButton
                        type="button"
                        icon="pi pi-refresh"
                        size="small"
                        :label="t('relaunch')"
                        :disabled="disabled || !getHasSelection"
                        @click="onClickRelaunch"
                    />
                </div>
            </div>
        </template>
    </BaseTitledCard>
</template>
